import { Link } from "react-router-dom";
import { Phone, MapPin, ChevronRight, Grid3x3, Bath, UtensilsCrossed, Home } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOMeta from "@/components/SEOMeta";
import LocalBusinessSchema from "@/components/LocalBusinessSchema";
import FAQ from "@/components/FAQ";

const faqData = [
  {
    question: "What tile materials do you work with?",
    answer: "We install all tile types including ceramic, porcelain, natural stone (marble, granite, travertine), glass mosaic, and large-format tiles. Each material is installed using appropriate techniques and products."
  },
  {
    question: "How long does tile installation take?",
    answer: "A standard bathroom takes 3-5 days including prep, tile setting, and grouting. Kitchen backsplashes are typically 1-2 days. Larger projects are estimated based on square footage and complexity."
  },
  {
    question: "Do you repair existing tile?",
    answer: "Yes, we offer tile repair including cracked tile replacement, grout repair and recoloring, and waterproofing fixes. Sometimes selective repairs can extend the life of your tile installation."
  },
  {
    question: "What's the difference between ceramic and porcelain?",
    answer: "Porcelain is denser and less porous than ceramic, making it more durable and better for high-moisture areas. We help you choose the right material for your specific application and budget."
  }
];

const tilingServices = [
  {
    title: "Bathroom Tile",
    description: "Complete bathroom tile installation including floors, walls, showers, and tub surrounds.",
    icon: Bath,
    features: ["Shower tile", "Floor tile", "Tub surrounds", "Waterproofing"]
  },
  {
    title: "Kitchen Backsplash",
    description: "Custom backsplash installation with precise cuts and professional finishing.",
    icon: UtensilsCrossed,
    features: ["Glass tile", "Subway tile", "Mosaic patterns", "Natural stone"]
  },
  {
    title: "Floor Tile",
    description: "Durable floor tile installation for kitchens, bathrooms, entryways, and commercial spaces.",
    icon: Grid3x3,
    features: ["Large format tile", "Heated floors", "Pattern layouts", "Commercial grade"]
  },
  {
    title: "Custom Features",
    description: "Decorative tile work including accent walls, borders, and artistic installations.",
    icon: Home,
    features: ["Accent walls", "Decorative borders", "Custom patterns", "Niche detailing"]
  }
];

const serviceAreas = [
  { name: "Niagara Region", link: "/locations" },
  { name: "Hamilton", link: "/locations/hamilton" },
  { name: "Toronto & GTA", link: "/locations/toronto" },
  { name: "Kitchener-Waterloo", link: "/locations/kitchener-waterloo" }
];

export default function TilingServices() {
  return (
    <>
      <SEOMeta
        title="Tile Installation & Remodeling | Niagara • Hamilton • GTA"
        description="Precision tile installation for bathrooms, kitchens and floors across Niagara, Hamilton & the GTA. Durable materials and clean lines."
        keywords="tile contractor Niagara, Hamilton tile install, GTA tile services, backsplash, ceramic tile, porcelain tile, bathroom tile"
        canonicalUrl="https://blueknightcontracting.ca/services/tiling"
      />
      <LocalBusinessSchema
        locationName="Tiling Services"
        addressLocality="Niagara Falls"
        addressRegion="ON"
        description="Professional tile installation for residential and commercial properties"
        services={["Bathroom Tile", "Kitchen Backsplash", "Floor Tile", "Shower Tile", "Custom Tile Work"]}
      />
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        {/* Hero Section */}
        <section className="relative py-20 px-4 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Professional Tile Installation
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Expert tiling services across Niagara, Hamilton & the GTA
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button asChild size="lg" className="gap-2">
                <Link to="/contact">
                  Get Free Quote <ChevronRight className="h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="gap-2">
                <a href="tel:+12899370797">
                  <Phone className="h-5 w-5" /> (289) 937-0797
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Tiling Services</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                From elegant backsplashes to durable floor installations, we deliver precision tile work
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {tilingServices.map((service, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle>{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <ChevronRight className="h-4 w-4 text-primary" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Why Choose Blue Knight?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle>Precision Work</CardTitle>
                  <CardDescription>
                    Meticulous attention to layout, level, and grout lines ensures a flawless finish every time
                  </CardDescription>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Quality Materials</CardTitle>
                  <CardDescription>
                    We use premium tile, waterproofing membranes, and adhesives for long-lasting installations
                  </CardDescription>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Expert Installers</CardTitle>
                  <CardDescription>
                    Our tile setters have years of experience with all tile types and complex pattern layouts
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Service Areas</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {serviceAreas.map((area, index) => (
                <Link key={index} to={area.link}>
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                    <CardHeader>
                      <MapPin className="h-8 w-8 text-primary mb-2" />
                      <CardTitle className="text-lg">{area.name}</CardTitle>
                    </CardHeader>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <FAQ faqs={faqData} />
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Card className="bg-gradient-to-br from-primary/10 to-accent/10">
              <CardHeader>
                <CardTitle className="text-3xl mb-4">Ready for Beautiful New Tile?</CardTitle>
                <CardDescription className="text-lg mb-6">
                  Contact us for a consultation and detailed tile installation quote
                </CardDescription>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild size="lg">
                    <Link to="/contact">Get Free Quote</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <a href="tel:+12899370797">(289) 937-0797</a>
                  </Button>
                </div>
              </CardHeader>
            </Card>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
